<template>
	<div data-component="bot-tricks-table">
		<dynamic-table
			:columns="getColumns"
			:data="getTricks"
			overflow="visible"
		>
			<template #trick="{ index }">
				<b>{{ getTrickIndex(index) }}</b>
			</template>
			<template #side="{ row, column, index }">
				<card-select-field
					renderOnChange
					hideSelected
					:id="getId(index, column)"
					:rules="getRules(row, column, index)"
					:options="getCardsOptionsWithText(column, index)"
					:disabled="getCardSelectionDisabled(index, column)"
					:value="getCardForSide(row, column)"
					@input="handleCardSelection($event, index, column)"
				/>
			</template>
			<template #condition="{ row, column, index }">
				<card-select-field
					v-if="getIsDummyTurn(row, column, index)"
					multiple
					renderOnChange
					hideSelected
					emptyAllowed
					:id="getId(index, column)"
					:rules="getConditionRules(row, column, index)"
					:options="getCardsOptionsWithText(column, index)"
					:value="getCardsForCondition(row, column)"
					@input="handleCardSelection($event, index, column)"
				/>
			</template>
			<template #winner="{ row, index }">
				<b>{{ getWinnerSide(row, index) }}</b>
			</template>
			<template #action="{ index }">
				<div data-element="actions-cell">
					<v2-button
						v-if="getCanClear(index)"
						fit
						size="xs"
						@click="handleClear(index)">
						Clear
					</v2-button>
					<v2-button
						v-if="getCanDelete(index)"
						fit
						size="xs"
						variant="red"
						@click="handleDelete(index)">
						Delete
					</v2-button>
				</div>
			</template>
		</dynamic-table>

		<div data-element="actions">
			<v2-button
				fit
				size="xs"
				:disabled="!getIfLastTrickSelected"
				@click="handleAddTrick">
				Add Trick
			</v2-button>
		</div>
	</div>
</template>

<script>

	import CardSelectField from '@/components/dealLibrary/CardSelectField';
	import DynamicTable from '@/components/ui/views/dynamicTable/DynamicTable';
	import V2Button from '@/components/v2/ui/V2Button.vue';
	import actionClick from '@/mixins/actionClick';
	import invalidFormMessage from '@/mixins/invalidFormMessage';
	import { partnerships, SEATS, uppercaseAlphabet } from '@/consts';
	import { createSuitElement, sortObjectKeys } from '@/helpers';
	import { Game } from '@bridge128/bridge128-game-logic-lib';

	export default {
		components: {
			V2Button,
			CardSelectField,
			DynamicTable
		},
		mixins: [actionClick, invalidFormMessage],
		props: {
			deal: {
				type: Object,
				default: undefined
			},
			condition: {
				type: Array,
				default () {
					return [];
				}
			},
			prevCondition: {
				type: Array,
				default: undefined
			},
			openingLead: {
				type: String,
				default: undefined
			},
			index: {
				type: Number,
				default: undefined
			}
		},
		data: () => ({
			suitsDataMap: {
				H: {
					symbol: '♥',
					colour: 'red'
				},
				D: {
					symbol: '♦',
					colour: 'red'
				},
				S: {
					symbol: '♠',
					colour: 'black'
				},
				C: {
					symbol: '♣',
					colour: 'black'
				}
			}
		}),
		computed: {
			getGameData () {
				if (this.deal) {
					return Game.fromState(this.deal.deal);
				}
				return null;
			},
			getDummyPlayer () {
				if (this.getGameData) {
					return this.getGameData.dummy;
				}
				return null;
			},
			getContract () {
				return this.deal?.contract;
			},
			getContractSuit () {
				if (!this.getContract) {
					return '';
				}
				return this.getContract[1];
			},
			getDeclarer () {
				return this.deal?.declarer;
			},
			getDefenders () {
				if (!this.getDeclarer) {
					return false;
				}
				const defenders = partnerships
					.find((partnership) => {
						return !partnership.includes(this.getDeclarer);
					})
					.map(seat => SEATS[seat]);

				const openerSide = SEATS[this.getDeclarer].left;

				if (defenders[0]?.short !== openerSide) {
					const opener = defenders[1];
					defenders[1] = defenders[0];
					defenders[0] = opener;
				}

				return defenders;
			},
			getFirstDefenderSide () {
				if (this.getDefenders?.length) {
					return this.getDefenders[0]?.short;
				}
				return undefined;
			},
			getSecondDefenderSide () {
				if (this.getDefenders?.length) {
					return this.getDefenders[1]?.short;
				}
				return undefined;
			},
			getColumns () {
				const columns = [
					{
						label: 'Trick',
						key: 'trick'
					}
				];

				if (this.getDefenders) {
					const [first, last] = this.getDefenders.map((defender, index) => {
						return {
							...defender,
							label: defender.name,
							key: 'side',
							index
						};
					});

					columns.push(
						first,
						{
							label: 'If Dummy plays',
							key: 'condition',
							short: first.left,
							right: first,
							left: last
						},
						last,
						{
							label: 'Winner',
							key: 'winner'
						},
						{
							label: '',
							key: 'action'
						}
					);
				}
				return columns;
			},
			getTricks () {
				if (this.condition?.length) {
					return this.condition;
				}
				return [];
			},
			getWinners () {
				if (this.getTricks.length) {
					let winners = [];
					const [firstTrick, ...tricks] = this.getTricks;
					const firstTrickWinner = this.getTrickWinner(firstTrick, this.getFirstDefenderSide);

					if (firstTrickWinner) {
						winners = [firstTrickWinner];

						if (tricks?.length) {
							for (const index in tricks) {
								const trick = tricks[index];
								const prevWinner = winners[index];
								const winner = this.getTrickWinner(trick, prevWinner);
								if (winner || winner === 0) {
									winners = [...winners, winner];
								}
							}
						}
					}

					return winners;
				}
				return [];
			},
			getIfLastTrickSelected () {
				if (this.getTricks.length) {
					const lastTrick = this.getTricks[this.getTricks.length - 1];
					return !!lastTrick[this.getFirstDefenderSide] && !!lastTrick[this.getSecondDefenderSide];
				}
				return false;
			}
		},
		watch: {
			getWinners: {
				handler () {
					this.getTricks.forEach((trick, index) => {
						const result = this.getIsDummyTurn(trick, {
							short: this.getDummyPlayer?.name,
							right: { short: this.getFirstDefenderSide }
						}, index);

						if (!result) {
							this.clearCondition(index);
						}
					});
				},
				deep: true
			}
		},
		created () {},
		methods: {
			getTrickIndex (index) {
				return `${index + 1}${uppercaseAlphabet[this.index]}`;
			},
			getCardsOptions (column, index) {
				if (this.getGameData) {
					const player = this.getGameData[`player${column.short}`];
					const excludeCards = this.getExcludedCards(column, index);
					return player.handAsList
						.map(({ name }) => name)
						.filter(card => !excludeCards.includes(card));
				}
				return [];
			},
			getCardsOptionsWithText (column, index) {
				const options = this.getCardsOptions(column, index);
				return options.map(name => {
					const cardValue = name[0];
					const cardSuit = name[1];
					const suit = this.suitsDataMap[cardSuit];
					const text = `<b>${cardValue}</b> ${createSuitElement(suit.symbol, suit.colour)}`;
					return {
						value: name,
						text
					};
				});
			},
			getCardForSide (row, column) {
				if (row && row[column.short]) {
					return row[column.short];
				}
			},
			getCardsForCondition (row, column) {
				if (this.getDummyPlayer?.name === column.short && row.condition?.length) {
					return row.condition;
				}
				return [];
			},
			getId (index, column) {
				return `card-${this.index}-${index}-${column.short}`;
			},
			getRules (row, column, index) {
				const leadSide = index === 0 ? this.getFirstDefenderSide : this.getWinners[index - 1];
				let trickSuit;
				const leadCard = row[leadSide];
				if (leadCard) {
					trickSuit = leadCard[1];
				}
				const hand = this.getCardsOptions(column, index);

				return {
					required: leadSide === column.short,
					canPlayCardToTrick: {
						trickSuit,
						hand
					}
				};
			},
			getConditionRules (row, column, index) {
				const leadSide = index === 0 ? this.getFirstDefenderSide : this.getWinners[index - 1];
				let trickSuit;
				const leadCard = row[leadSide];
				if (leadCard) {
					trickSuit = leadCard[1];
				}
				const tricks = this.getTricks;
				const prevTricks = tricks.slice(0, index);
				const conditionsCards = prevTricks.map(trick => trick.condition).flat();
				const currentValue = this.getCardsForCondition(row, column);
				const prevConditionsCards = this.removeValues(conditionsCards, currentValue);
				const hand = this.getCardsOptions(column, index);
				const availableHand = this.removeValues(hand, prevConditionsCards);

				return {
					canPlayCardsToTrick: {
						trickSuit,
						hand: availableHand
					}
				};
			},
			getExcludedCards (column, index) {
				return this.getTricks
					.map((trick, trickIndex) => index === trickIndex ? false : trick[column.short])
					.filter(Boolean);
			},
			compareTricks (trickA, trickB) {
				if (trickA && trickB) {
					return JSON.stringify(sortObjectKeys(trickA)) === JSON.stringify(sortObjectKeys(trickB));
				}
				return false;
			},
			getCardSelectionDisabled (index, column) {
				if (!this.openingLead) {
					return true;
				}
				if (this.prevCondition?.length && column.short === this.getSecondDefenderSide) {
					const trick = this.getTricks[index];
					const trickFromPrevCondition = this.prevCondition[index];
					return this.compareTricks(trick, trickFromPrevCondition);
				}

				return index === 0 && column.short === this.getFirstDefenderSide;
			},
			getIsDummyTurn (row, column, index) {
				if (this.getDummyPlayer?.name !== column.short) {
					return false;
				}
				if (index === 0) {
					return true;
				} else {
					const prevTrickIndex = index - 1;
					const prevTrick = this.getTricks[prevTrickIndex];
					return column.right.short === this.getWinnerSide(prevTrick, prevTrickIndex);
				}
			},
			clearCondition (index) {
				this.handleCardSelection([],  index, { short: this.getDummyPlayer?.name });
			},
			handleCardSelection (card, index, column) {
				this.$emit('onCardSelection', this.index, index, card, column);
			},
			handleAddTrick () {
				if (this.getIfLastTrickSelected) {
					this.$emit('onAddTrick', this.index);
				}
			},
			parseCard (cardString) {
				if (cardString && this.getGameData) {
					return this.getGameData.deck.card(cardString);
				}
			},
			getTrickWinner (trick, prevTrickWinner) {
				const firstSide =  this.getFirstDefenderSide;
				const secondSide = this.getSecondDefenderSide;

				if (trick[firstSide] && trick[secondSide]) {
					const leadSide = prevTrickWinner;
					const partnerSide = prevTrickWinner === firstSide ? secondSide : firstSide;

					const leadCard = this.parseCard(trick[leadSide]);
					const partnerCard = this.parseCard(trick[partnerSide]);
					const sameSuits = leadCard.suitName === partnerCard.suitName;

					if (sameSuits) {
						return leadCard.isHigherThan(partnerCard) ? leadSide : partnerSide;
					} else if (leadCard.suitName === this.getContractSuit) {
						return leadSide;
					} else if (partnerCard.suitName === this.getContractSuit) {
						return partnerSide;
					}
					return leadSide;
				}
				return null;
			},
			getWinnerSide (trick, index) {
				if (this.getWinners?.length && this.getWinners[index]) {
					return this.getWinners[index];
				}
				return '';
			},
			getCanClear (index) {
				const tricksMaxIndex = this.getTricks.length - 1;
				return tricksMaxIndex === index;
			},
			getCanDelete (index) {
				if (index === 0) {
					if (this.index > 0) {
						return this.getCanClear(index);
					}
					return false;
				}
				return this.getCanClear(index);
			},
			removeValues (arr, valuesToRemove) {
				return arr.filter(item => !valuesToRemove.includes(item));
			},
			handleClear (index) {
				this.$emit('onClear', this.index, index);
			},
			handleDelete (index) {
				this.$emit('onDelete', this.index, index);
			}
		}
	};
</script>

<style lang="scss" scoped>
	[data-component="bot-tricks-table"] {
		::v-deep [data-component='dynamic-table'] {
			@media (max-width: 1279px) {
				overflow: auto !important;
				min-height: 225px;
			}

			[data-element='table-column-side'] {
				[data-component='select-field'] {
					margin: 0;
				}
			}

			[data-element='table-column-trick'],
			[data-element='table-column-side'],
			[data-element='table-column-condition'],
			[data-element='table-column-winner'],
			[data-element='table-column-action'] {
				vertical-align: top;
			}

			[data-element='table-column-winner'] {
				text-align: center;
			}

			[data-element='table-column-trick'] {
				width: 50px;
			}
			[data-element='table-column-winner'] {
				width: 70px;
			}

			[data-element='table-column-condition'] {
				width: 100%;
				padding: rem(6) 10px;
				text-align: left;

				@include mq($below: 'sm') {
					width: 160px;
				}

				[data-component='select-field'] {
					margin: 0;
					width: 100%;

					.multiselect {
						&__tags-wrap {
							display: inline-flex;
							flex-wrap: wrap;
						}
					}
				}
			}

			[data-element='table-column-side'] {
				width: 90px;
				text-align: left;

				[data-component='select-field'] {
					margin: 0;
					width: 100%;
				}
			}

			td {
				&[data-element='table-column-trick'],
				&[data-element='table-column-winner'] {
					padding-top: rem(15);
				}
				&[data-element='table-column-action'] {
					padding-top: rem(12);
				}
			}

			[data-element='table-column-action'] {
				width: 110px;

				[data-element='actions-cell'] {
					display: flex;
					justify-content: flex-end;

					.btn:not(:first-child) {
						margin-left: 6px;
					}
				}
			}
		}

		[data-element="actions"] {
			margin: 10px 0;

			[data-component='btn'] {
				width: fit-content;
			}
		}
	}
</style>
